import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Users, Clock, Loader2, Bell, KeyRound, MessageSquare, CheckCircle, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContractorOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [usersRes, timesheetsRes, requestsRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/timesheets`),
        axios.get(`${API}/worker-requests`)
      ]);

      const workers = usersRes.data.filter(u => u.user_type === "worker");
      const activeWorkers = workers.filter(w => w.is_active).length;
      const pendingRequests = requestsRes.data.filter(r => r.status === "pending").length;
      const confirmedRequests = requestsRes.data.filter(r => r.status === "confirmed").length;

      setStats({
        totalWorkers: workers.length,
        activeWorkers,
        totalTimesheets: timesheetsRes.data.length,
        pendingRequests,
        confirmedRequests
      });
    } catch (error) {
      toast.error("Failed to load dashboard stats");
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const response = await axios.post(`${API}/reminders/send-bulk`);
      if (response.data.sent_count > 0) {
        toast.success(`Sent ${response.data.sent_count} reminder${response.data.sent_count > 1 ? 's' : ''} to workers`);
      } else {
        toast.info("No workers need reminders at this time");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reminders");
    } finally {
      setSendingReminders(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }
    if (passwordData.new_password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success("Password changed successfully");
      setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#F37021]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-[#18181B]">Contractor Dashboard</h1>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-[#52525B]">Team Members</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-[#18181B]">{stats?.totalWorkers || 0}</p>
                <p className="text-xs text-[#71717A]">{stats?.activeWorkers || 0} active</p>
              </div>
              <Users className="w-8 h-8 text-[#F37021]" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-[#52525B]">Timesheets</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-[#18181B]">{stats?.totalTimesheets || 0}</p>
                <p className="text-xs text-[#71717A]">total entries</p>
              </div>
              <Clock className="w-8 h-8 text-[#F37021]" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-[#52525B]">Pending Requests</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-[#18181B]">{stats?.pendingRequests || 0}</p>
                <p className="text-xs text-[#71717A]">awaiting response</p>
              </div>
              <XCircle className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-[#52525B]">Confirmed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-[#18181B]">{stats?.confirmedRequests || 0}</p>
                <p className="text-xs text-[#71717A]">active jobs</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Send Reminders Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#F37021]" />
              Send Timesheet Reminders
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-[#52525B] mb-4">
              Send email reminders to all workers who have not submitted timesheets for the current period.
            </p>
            <Button 
              onClick={handleSendReminders}
              disabled={sendingReminders}
              className="bg-[#F37021] hover:bg-[#D95D15] text-white"
              data-testid="send-reminders-button"
            >
              {sendingReminders ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
              ) : (
                <><MessageSquare className="w-4 h-4 mr-2" /> Send Reminders</>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Change Password Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-[#F37021]" />
              Change Password
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordChange} className="space-y-3">
              <div>
                <Label className="text-xs text-[#52525B]">Current Password</Label>
                <Input 
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label className="text-xs text-[#52525B]">New Password</Label>
                <Input 
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label className="text-xs text-[#52525B]">Confirm New Password</Label>
                <Input 
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                  required
                />
              </div>
              <Button 
                type="submit" 
                disabled={changingPassword}
                className="w-full bg-[#F37021] hover:bg-[#D95D15]"
                data-testid="change-password-button"
              >
                {changingPassword ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Changing...</>
                ) : (
                  "Change Password"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ContractorOverview;
